import React, { useState } from 'react';
import { Plus, Edit, Trash2, Eye, Video, Save, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface TutorialVideo {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  category: string;
  duration?: string;
  tags: string[];
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const VideosTab: React.FC = () => {
  const [videos, setVideos] = useState<TutorialVideo[]>(() => {
    const saved = localStorage.getItem('tutorial-videos');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Erro ao carregar vídeos salvos:', error);
      }
    }
    // Vídeos padrão apenas na primeira vez
    return [
      {
        id: '1',
        title: 'Como usar o Chat Jurídico com IA',
        description: 'Aprenda a fazer perguntas eficientes e obter respostas precisas do nosso assistente jurídico.',
        youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        category: 'ai-tools',
        duration: '8:45',
        tags: ['chat', 'ia', 'perguntas'],
        order: 1,
        isActive: true,
        createdAt: '2024-01-15',
        updatedAt: '2024-01-15',
      },
      {
        id: '2',
        title: 'Criando Documentos Profissionais',
        description: 'Passo a passo para criar documentos jurídicos usando nossos templates.',
        youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        category: 'ai-tools',
        duration: '12:30',
        tags: ['documentos', 'templates', 'criação'],
        order: 2,
        isActive: true,
        createdAt: '2024-01-16',
        updatedAt: '2024-01-16',
      },
    ];
  });

  const [editingVideo, setEditingVideo] = useState<TutorialVideo | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const categories = [
    { value: 'painel', label: 'Painel & Notificações' },
    { value: 'ai-tools', label: 'Ferramentas com IA' },
    { value: 'calculations', label: 'Cálculos & Ferramentas' },
    { value: 'settings', label: 'Configurações & Planos' },
    { value: 'legal', label: 'Legal & Atualizações' },
  ];

  const handleCreateVideo = () => {
    const newVideo: TutorialVideo = {
      id: Date.now().toString(),
      title: '',
      description: '',
      youtubeUrl: '',
      category: 'ai-tools',
      duration: '',
      tags: [],
      order: videos.length + 1,
      isActive: true,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
    };
    setEditingVideo(newVideo);
    setIsDialogOpen(true);
  };

  const handleEditVideo = (video: TutorialVideo) => {
    setEditingVideo({ ...video });
    setIsDialogOpen(true);
  };

  const handleSaveVideo = () => {
    if (!editingVideo) return;

    let updatedVideos;
    if (videos.find(v => v.id === editingVideo.id)) {
      // Update existing video
      updatedVideos = videos.map(v => v.id === editingVideo.id ? editingVideo : v);
    } else {
      // Add new video
      updatedVideos = [...videos, editingVideo];
    }

    setVideos(updatedVideos);
    localStorage.setItem('tutorial-videos', JSON.stringify(updatedVideos));

    setEditingVideo(null);
    setIsDialogOpen(false);
  };

  const handleDeleteVideo = (id: string) => {
    if (confirm('Tem certeza que deseja deletar este vídeo?')) {
      const updatedVideos = videos.filter(v => v.id !== id);
      setVideos(updatedVideos);
      localStorage.setItem('tutorial-videos', JSON.stringify(updatedVideos));
    }
  };

  const handleToggleActive = (id: string) => {
    const updatedVideos = videos.map(v => 
      v.id === id ? { ...v, isActive: !v.isActive } : v
    );
    setVideos(updatedVideos);
    localStorage.setItem('tutorial-videos', JSON.stringify(updatedVideos));
  };

  const extractVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : '';
  };

  const getEmbedUrl = (url: string) => {
    const videoId = extractVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Gestão de Vídeos Tutoriais
              </CardTitle>
              <CardDescription>
                Gerencie os vídeos explicativos que aparecem na página de tutoriais
              </CardDescription>
            </div>
            <Button onClick={handleCreateVideo}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Vídeo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {videos.map((video) => (
                  <TableRow key={video.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{video.title}</p>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {video.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {categories.find(c => c.value === video.category)?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{video.duration}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {video.tags.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {video.tags.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{video.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={video.isActive}
                        onCheckedChange={() => handleToggleActive(video.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditVideo(video)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteVideo(video.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog for Create/Edit Video */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingVideo?.id && videos.find(v => v.id === editingVideo.id) ? 'Editar Vídeo' : 'Novo Vídeo'}
            </DialogTitle>
            <DialogDescription>
              Preencha as informações do vídeo tutorial
            </DialogDescription>
          </DialogHeader>
          
          {editingVideo && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={editingVideo.title}
                    onChange={(e) => setEditingVideo({
                      ...editingVideo,
                      title: e.target.value
                    })}
                    placeholder="Título do vídeo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Categoria</Label>
                  <Select 
                    value={editingVideo.category} 
                    onValueChange={(value) => setEditingVideo({
                      ...editingVideo,
                      category: value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={editingVideo.description}
                  onChange={(e) => setEditingVideo({
                    ...editingVideo,
                    description: e.target.value
                  })}
                  placeholder="Descrição do vídeo"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="youtubeUrl">URL do YouTube</Label>
                  <Input
                    id="youtubeUrl"
                    value={editingVideo.youtubeUrl}
                    onChange={(e) => setEditingVideo({
                      ...editingVideo,
                      youtubeUrl: e.target.value
                    })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duração</Label>
                  <Input
                    id="duration"
                    value={editingVideo.duration || ''}
                    onChange={(e) => setEditingVideo({
                      ...editingVideo,
                      duration: e.target.value
                    })}
                    placeholder="ex: 8:45"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
                <Input
                  id="tags"
                  value={editingVideo.tags.join(', ')}
                  onChange={(e) => setEditingVideo({
                    ...editingVideo,
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                  })}
                  placeholder="chat, ia, perguntas"
                />
              </div>

              {/* Preview do vídeo */}
              {editingVideo.youtubeUrl && (
                <div className="space-y-2">
                  <Label>Preview do Vídeo</Label>
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <iframe
                      src={getEmbedUrl(editingVideo.youtubeUrl)}
                      title="Preview"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button onClick={handleSaveVideo}>
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideosTab;